import { EFieldTypes, ESqlOperators } from 'scw-digital-boards-widgets';
import { IWorkspaceModules } from '../../../pages/settings/dashboards/dashboards.interface';
import { TAdvancedFilterConfig } from './advanced-filter.interface';

export const ADVANCED_FILTER_FIELDS = {
  actionItemType: [{ type: EFieldTypes.predefined, operators: [ESqlOperators.$in] }],
  activityId: [{ type: EFieldTypes.apiDropdown, operators: [ESqlOperators.$in] }],
  boardId: [{ type: EFieldTypes.apiDropdown, operators: [ESqlOperators.$in] }],
  commentTag: [{ type: EFieldTypes.apiDropdown, operators: [ESqlOperators.$in] }],
  date: [
    { type: EFieldTypes.date, operators: [ESqlOperators.$between] },
    { type: EFieldTypes.apiDropdown, operators: [ESqlOperators.$currentShift, ESqlOperators.$previousShift] },
  ],
  decisionDateType: [{ type: EFieldTypes.predefined, operators: [ESqlOperators.$in] }],
  itemCategoryId: [{ type: EFieldTypes.apiDropdown, operators: [ESqlOperators.$in] }],
  jobId: [{ operators: [ESqlOperators.$in], type: EFieldTypes.apiDropdown }],
  lineId: [{ type: EFieldTypes.apiDropdown, operators: [ESqlOperators.$in] }],
  shiftId: [
    { type: EFieldTypes.apiDropdown, operators: [ESqlOperators.$in] },
    { type: EFieldTypes.apiDropdown, operators: [ESqlOperators.$currentShift, ESqlOperators.$previousShift] },
  ],
  siteIds: [{ type: EFieldTypes.apiDropdown, operators: [ESqlOperators.$in] }],
  statusCategory: [{ type: EFieldTypes.predefined, operators: [ESqlOperators.$in] }],
  taskName: [{ type: EFieldTypes.apiDropdown, operators: [ESqlOperators.$in] }],
  workflowStep: [{ type: EFieldTypes.apiDropdown, operators: [ESqlOperators.$in] }],
  workOrderId: [{ type: EFieldTypes.apiDropdown, operators: [ESqlOperators.$in] }],
} satisfies Record<string, Pick<TAdvancedFilterConfig, 'type' | 'operators'>[]>;

export type TAdvancedFilterFieldName = keyof typeof ADVANCED_FILTER_FIELDS;

/** Looks up a field's operators by `type`. Only safe when a field has at most one `configurations` entry per
 * `type`. `date`'s two entries differ by type (`date` vs `apiDropdown`), so this works for both of them —
 * but `shiftId`'s `$currentShift`/`$previousShift` entry shares `EFieldTypes.apiDropdown` with its real-id
 * `$in` entry, so this helper can't disambiguate between them; the resolver writes that one's operators
 * literally instead of calling this. */
export const getOperatorsForAdvancedFilterField = <K extends TAdvancedFilterFieldName>(
  field: K,
  fieldType: EFieldTypes,
): TAdvancedFilterConfig['operators'] => {
  return ADVANCED_FILTER_FIELDS[field].find(({ type }) => type === fieldType)!.operators;
};

export enum EValueType {
  raw = 'raw',
  display = 'display',
}

export const ADVANCED_FILTER_FIELD_VALUE_TYPES: Record<TAdvancedFilterFieldName, ReadonlyArray<EValueType>> = {
  actionItemType: [EValueType.raw, EValueType.display],
  activityId: [EValueType.raw, EValueType.display],
  boardId: [EValueType.raw, EValueType.display],
  commentTag: [EValueType.raw, EValueType.display],
  date: [EValueType.raw],
  decisionDateType: [EValueType.raw, EValueType.display],
  itemCategoryId: [EValueType.raw, EValueType.display],
  jobId: [EValueType.raw, EValueType.display],
  lineId: [EValueType.raw, EValueType.display],
  shiftId: [EValueType.raw, EValueType.display],
  siteIds: [EValueType.raw, EValueType.display],
  statusCategory: [EValueType.raw, EValueType.display],
  taskName: [EValueType.display],
  workflowStep: [EValueType.raw, EValueType.display],
  workOrderId: [EValueType.raw, EValueType.display],
};

export const ADVANCED_FILTER_FIELD_MODULES: Record<TAdvancedFilterFieldName, ReadonlyArray<keyof IWorkspaceModules>> = {
  actionItemType: ['actionTracker'],
  activityId: ['oeeTracker'],
  boardId: ['actionTracker'],
  commentTag: ['oeeTracker'],
  date: [],
  decisionDateType: ['actionTracker'],
  itemCategoryId: ['actionTracker'],
  jobId: ['oeeTracker'],
  lineId: ['oeeTracker'],
  shiftId: ['oeeTracker', 'actionTracker'],
  siteIds: ['oeeTracker'],
  statusCategory: ['actionTracker'],
  taskName: ['oeeTracker'],
  workflowStep: ['actionTracker'],
  workOrderId: ['oeeTracker'],
};
